/**
 * CM Bet Ticker — `POST /v1/book/ticker` (Postgres) or `/v1/book/tickerBQ` (BigQuery).
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cmApi,
  type BookTickerFilterOptionsResponse,
  type BookTickerRequest,
  type BookTickerResponse,
  type BookTickerRow,
  type FlagBetRequest,
} from '@/api/cmApi'
import { useCmBookUser } from '@/api/hooks/net-exposure/useBooks'
import { useBookmakerSessionFlag } from '@/api/hooks/useCmMe'
import { authHeaders } from '@/auth/session'
import { ROLE_LABEL } from '@/features/hierarchy/hierarchy'
import type {
  TickerBet,
  TickerFilterOptions,
  TickerFilters,
  TickerPage,
  TickerStatusScope,
} from '@/features/bet-ticker/domain'
import { orderStakeBounds } from '@/features/bet-ticker/filters'
import { marketNameForRequest } from '@/features/bet-ticker/marketType'
import {
  tickerMarketTypeForFlag,
  type BookmakerSessionFlag,
} from '@/lib/bookmakerSessionFlag'

export const TICKER_POLL_MS = 5_000

const CM_LEVEL = ROLE_LABEL['company-master']

export interface TickerQuery {
  statusScope: TickerStatusScope
  filters: TickerFilters
  /** Lab `/betfair-bet-ticker` → CM-API `source: 'betfair'`. */
  source?: BookTickerRequest['source']
  /**
   * `bq` → `/v1/book/tickerBQ`;
   * `flag-final-bq` → `/v1/book/tickerFlagFinalBQ`;
   * default Postgres `ticker`.
   */
  dataSource?: 'pg' | 'bq' | 'flag-final-bq'
  enabled?: boolean
}

function dayStart(day: string): string {
  return `${day}T00:00:00.000Z`
}

function dayEnd(day: string): string {
  return `${day}T23:59:59.999Z`
}

/** Effective Lab marketType after staff Market access (when chip is All). */
export function effectiveTickerMarketType(
  filters: TickerFilters,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): string {
  if (filters.marketType !== 'all') return filters.marketType
  return tickerMarketTypeForFlag(bookmakerSessionFlag) ?? 'all'
}

export function buildTickerRequest(
  query: TickerQuery,
  user: string,
  bookmakerSessionFlag: BookmakerSessionFlag = 'all',
): BookTickerRequest {
  const { filters } = query
  const marketType = effectiveTickerMarketType(filters, bookmakerSessionFlag)
  const marketName = marketNameForRequest(filters.market, marketType)
  return {
    user,
    accessLevel: CM_LEVEL,
    source: query.source ?? 'sportsbook',
    statusScope: query.statusScope,
    page: filters.page,
    pageSize: filters.pageSize,
    ...(filters.tmId ? { tmId: filters.tmId } : {}),
    ...(filters.from ? { fromDate: dayStart(filters.from) } : {}),
    ...(filters.to ? { toDate: dayEnd(filters.to) } : {}),
    // Lab multi ui-select → exact array; legacy `q` → substring LIKE.
    ...(filters.members.length
      ? { userName: filters.members }
      : filters.search
        ? { userName: filters.search }
        : {}),
    ...(filters.sport !== 'all' ? { eventType: filters.sport } : {}),
    ...(filters.event ? { eventName: filters.event } : {}),
    ...(marketName ? { marketName } : {}),
    ...(filters.currency !== 'all' ? { currencyType: filters.currency } : {}),
    ...(filters.side !== 'all' ? { side: filters.side } : {}),
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
    ...stakeRangeForRequest(filters.stakeMin, filters.stakeMax),
    ...(filters.flagged === 'flagged' ? { flaggedBets: true } : {}),
    ...(filters.flagged === 'final' ? { finallyFlaggedBets: true } : {}),
    // CM-API classifies product_type/metadata (same as books) — not market-name heuristics.
    ...(bookmakerSessionFlag !== 'all'
      ? { bookmakerSessionFlag }
      : {}),
  }
}

/** Order stake bounds for the API — UI inputs reorder on blur via orderStakeBounds. */
export function stakeRangeForRequest(
  stakeMin: number | null,
  stakeMax: number | null,
): Pick<BookTickerRequest, 'stakeFrom' | 'stakeTo'> {
  const { stakeMin: from, stakeMax: to } = orderStakeBounds(stakeMin, stakeMax)
  return {
    ...(from != null ? { stakeFrom: from } : {}),
    ...(to != null ? { stakeTo: to } : {}),
  }
}

/** Map CM-API ticker row → UI bet (side coerce + numeric defaults). */
export function toTickerBet(row: BookTickerRow): TickerBet {
  return {
    id: row.betId,
    placedAt: row.placedAt,
    matchedAt: row.matchedAt,
    memberId: row.memberId ?? '',
    memberUsername: row.userName ?? '',
    sport: row.eventType ?? '',
    eventName: row.eventName ?? '',
    marketId: row.marketId ?? '',
    marketName: row.marketName ?? '',
    selection: row.selectionName ?? '',
    side: String(row.side).toUpperCase() === 'LAY' ? 'LAY' : 'BACK',
    currency: row.currencyType ?? '',
    odds: Number(row.odds) || 0,
    oddsPlaced: Number(row.oddsPlaced ?? row.odds) || 0,
    oddsMatched: Number(row.oddsMatched ?? row.odds) || 0,
    matchedAmount: Number(row.matchedAmount) || 0,
    unmatchedAmount: Number(row.unmatchedAmount) || 0,
    profitLiability: Number(row.profitLiability) || 0,
    amountPlacedOnBetfair: Number(row.amountPlacedOnBetfair) || 0,
    amountExecuted: Number(row.amountExecuted) || 0,
    amountPending: Number(row.amountPending) || 0,
    amountCancelled: Number(row.amountCancelled) || 0,
    multiplier: Number(row.multiplier) > 0 ? Number(row.multiplier) : 1,
    clientIp: row.clientIp || null,
    smartUser: Boolean(row.smartUser),
    isSmartUserByCron: Boolean(row.isSmartUserByCron),
    smartUserComment: (() => {
      const c = row.smartUserComment
      if (typeof c !== 'string') return null
      const t = c.trim()
      return t || null
    })(),
    doubtfull: Boolean(row.doubtfull),
    watchlist: Boolean(row.watchlist),
    isFlagged: Boolean(row.isFlagged),
    isFinalFlagged: Boolean(row.isFinalFlagged),
    isFlaggedBy: row.isFlaggedBy ?? '',
    isFinalFlaggedBy: row.isFinalFlaggedBy ?? '',
    comment: row.comment ?? '',
    finalFlagCategory: row.finalFlagCategory ?? '',
    reason: row.reason ?? '',
    isBetfairFlagged: Boolean(row.isBetfairFlagged),
  }
}

function toPage(res: BookTickerResponse, filters: TickerFilters): TickerPage {
  return {
    rows: (res?.rows ?? []).map(toTickerBet),
    total: res?.total ?? 0,
    page: res?.page ?? filters.page,
    pageSize: res?.pageSize ?? filters.pageSize,
    asOf: res?.asOf ?? new Date().toISOString(),
  }
}

function filterKey(filters: TickerFilters): string {
  return JSON.stringify({
    tmId: filters.tmId,
    members: filters.members,
    search: filters.search,
    sport: filters.sport,
    event: filters.event,
    market: filters.market,
    marketType: filters.marketType,
    side: filters.side,
    status: filters.status,
    currency: filters.currency,
    flagged: filters.flagged,
    from: filters.from,
    to: filters.to,
    stakeMin: filters.stakeMin,
    stakeMax: filters.stakeMax,
    pageSize: filters.pageSize,
  })
}

export function useBookTicker(query: TickerQuery) {
  const userQ = useCmBookUser()
  const user = userQ.data
  const bookmakerSessionFlag = useBookmakerSessionFlag()
  const enabled = (query.enabled ?? true) && Boolean(user)
  const poll = enabled && query.statusScope === 'current'

  const dataSource = query.dataSource ?? 'pg'

  return useQuery({
    queryKey: [
      'cm',
      'book-ticker',
      dataSource,
      query.source ?? 'sportsbook',
      query.statusScope,
      user,
      filterKey(query.filters),
      query.filters.page,
      bookmakerSessionFlag,
    ],
    queryFn: async () => {
      const body = buildTickerRequest(query, user!, bookmakerSessionFlag)
      const headers = authHeaders()
      const res =
        dataSource === 'flag-final-bq'
          ? await cmApi.bookTickerFlagFinalBq(headers, body)
          : dataSource === 'bq'
            ? await cmApi.bookTickerBq(headers, body)
            : await cmApi.bookTicker(headers, body)
      return toPage(res, query.filters)
    },
    enabled,
    staleTime: TICKER_POLL_MS,
    refetchInterval: poll ? TICKER_POLL_MS : false,
    placeholderData: keepPreviousData,
  })
}

export function useBookTickerFilterOptions(query: {
  statusScope: TickerStatusScope
  from?: string
  to?: string
  tmId?: string
  source?: BookTickerRequest['source']
  /** `bq` / `flag-final-bq` → BigQuery filter-options paths. */
  dataSource?: 'pg' | 'bq' | 'flag-final-bq'
  /** Lab subGame — cascade events when Sub Game ≠ All. */
  eventType?: string
  /** Lab selectedEvent — cascade markets when Event set. */
  eventName?: string
  enabled?: boolean
}) {
  const userQ = useCmBookUser()
  const user = userQ.data
  const enabled = (query.enabled ?? true) && Boolean(user)
  const dataSource = query.dataSource ?? 'pg'

  return useQuery({
    queryKey: [
      'cm',
      'book-ticker-filter-options',
      dataSource,
      query.source ?? 'sportsbook',
      query.statusScope,
      user,
      query.tmId ?? '',
      query.from ?? '',
      query.to ?? '',
      query.eventType ?? '',
      query.eventName ?? '',
    ],
    queryFn: async (): Promise<TickerFilterOptions> => {
      const body = {
        user: user!,
        accessLevel: CM_LEVEL,
        source: query.source ?? 'sportsbook',
        statusScope: query.statusScope,
        ...(query.tmId ? { tmId: query.tmId } : {}),
        ...(query.from ? { fromDate: dayStart(query.from) } : {}),
        ...(query.to ? { toDate: dayEnd(query.to) } : {}),
        ...(query.eventType ? { eventType: query.eventType } : {}),
        ...(query.eventName ? { eventName: query.eventName } : {}),
      }
      const headers = authHeaders()
      const res: BookTickerFilterOptionsResponse =
        dataSource === 'flag-final-bq'
          ? await cmApi.bookTickerFlagFinalBqFilterOptions(headers, body)
          : dataSource === 'bq'
            ? await cmApi.bookTickerBqFilterOptions(headers, body)
            : await cmApi.bookTickerFilterOptions(headers, body)
      return {
        sports: res.sports ?? [],
        events: res.events ?? [],
        markets: res.markets ?? [],
      }
    },
    enabled,
    staleTime: 60_000,
  })
}

/** Lab flagBet — invalidate ticker lists on success. */
export function useFlagBet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: FlagBetRequest) => cmApi.flagBet(authHeaders(), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cm', 'book-ticker'] })
    },
  })
}
